// src/modules/competitions/components/JudgeTermsModal.tsx

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { judgeService } from '../services/judgeService';
import { JudgeTerms } from '../types/judge';

interface JudgeTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  eventTitle: string;
  onAccept: (termsId: number) => void;
}

const JudgeTermsModal: React.FC<JudgeTermsModalProps> = ({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  onAccept
}) => {
  const [terms, setTerms] = useState<JudgeTerms | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadTerms();
    }
  }, [isOpen, eventId]);

  useEffect(() => {
    checkScrollNeeded();
  }, [terms]);

  const loadTerms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await judgeService.getTerms(eventId);
      
      if (response.success && response.data) {
        setTerms(response.data);
      } else {
        setError(response.message || 'Error al cargar términos');
      }
    } catch (err) {
      setError('Error al cargar términos y condiciones');
    } finally {
      setIsLoading(false);
    }
  };

  const checkScrollNeeded = () => {
    if (termsRef.current) {
      const element = termsRef.current;
      if (element.scrollHeight <= element.clientHeight + 10) {
        setHasScrolled(true);
      }
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    
    if (bottom && !hasScrolled) {
      setHasScrolled(true);
    }
  };

  const handleAccept = () => {
    if (!terms || !agreed || !hasScrolled) return;
    onAccept(terms.id);
  };

  const canAccept = agreed && hasScrolled && !isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Términos y Condiciones para Jueces"
      size="large"
      closeOnBackdrop={false}
    >
      {isLoading ? (
        <Loading message="Cargando términos..." />
      ) : error ? (
        <ErrorMessage message={error} type="error" />
      ) : terms ? (
        <div className="judge-terms-content">
          {/* Info header */}
          <div className="terms-header">
            <h3>🏆 {eventTitle}</h3>
            <p className="terms-notice">
              ⚠️ Al aceptar estos términos podrás postular como juez en cualquier 
              categoría de esta competencia.
            </p>
          </div>

          {/* Contenido scrolleable */}
          <div 
            ref={termsRef}
            className="terms-text" 
            onScroll={handleScroll}
          >
            <div dangerouslySetInnerHTML={{ __html: terms.content }} />
          </div>

          {/* Indicador de scroll */}
          {!hasScrolled && (
            <div className="scroll-indicator">
              ⬇️ Desplázate hasta el final para continuar
            </div>
          )}

          {/* Checkbox de aceptación */}
          <div className="terms-acceptance">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={!hasScrolled}
              />
              <span>
                He leído y acepto los términos y condiciones para actuar como juez
              </span>
            </label>
          </div>

          {/* Acciones */}
          <div className="terms-actions">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={!canAccept}
            >
              Aceptar y Continuar
            </Button>
          </div>
        </div>
      ) : (
        <p>No se encontraron términos y condiciones</p>
      )}
    </Modal>
  );
};

export default JudgeTermsModal;